import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import UniversityService from "../../../services/KangarooServices/UniversityService";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import DestinationService from "../../../services/KangarooServices/DestinationService";
import MediaModal from "../../PagesSection/Component/MediaModal";
import MediaService from "../../../services/KangarooServices/MediaServices";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import toast from "react-hot-toast";
import { imageBaseApi } from "../../../utilis/imageHelper";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import ColorPicker from "../../../Components/ColorPicker/ColorPicker";

const tabs = ["Basic", "Content", "IsOnshoreContent", "Media", "Settings"];

const AddInstitute = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("Basic");
  const [institutedata, setInstituteData] = useState(null);
  const [destinationdata, setDestinationData] = useState([]);
  const [mediaData, setMediaData] = useState([]);

  const [logoMedia, setLogoMedia] = useState(null);
  const [bannerMedia, setBannerMedia] = useState(null);
  const [activeMediaType, setActiveMediaType] = useState(null);
  const [isMediaOpen, setIsMediaOpen] = useState(false);

  const [slugMessage, setSlugMessage] = useState("");
  const [isSlugValid, setIsSlugValid] = useState(true);

  /* ---------------- HELPERS ---------------- */
  const generateSlug = (text) =>
    text
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-");

  const checkSlug = async (slug) => {
    if (!slug) return;
    try {
      const res = await UniversityService.checkSlug({ slug });
      setIsSlugValid(!res.exists);
      setSlugMessage(
        res.exists
          ? `Slug taken. Suggested: ${res.suggestedSlug}`
          : "Slug available",
      );
    } catch {
      setIsSlugValid(false);
      setSlugMessage("Unable to check slug");
    }
  };

  /* ---------------- FETCH ---------------- */
  useEffect(() => {
    DestinationService.getList().then(setDestinationData);
    MediaService.getList({ page: 1, pageSize: 1000 }).then(setMediaData);
  }, []);

  useEffect(() => {
    if (!id) return;

    const loadInstitute = async () => {
      const res = await UniversityService.getbyId(id);
      setInstituteData(res);

      // 🔥 DIRECTLY USE RESPONSE MEDIA OBJECTS
      setLogoMedia(res.logoMedia || null);
      setBannerMedia(res.bannerMedia || null);
    };

    loadInstitute();
  }, [id]);

  /* ---------------- FORMIK ---------------- */
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      destinationCountryId: institutedata?.destinationCountryId || "",
      name: institutedata?.name || "",
      slug: institutedata?.slug || "",
      city: institutedata?.city || "",
      ranking: institutedata?.ranking || "",
      websiteUrl: institutedata?.websiteUrl || "",
      applyUrl: institutedata?.applyUrl || "",
      programmesUrl: institutedata?.programmesUrl || "",
      buttonUrl: institutedata?.buttonUrl || "",
      videoUrl: institutedata?.videoUrl || "",
      orderIndex: institutedata?.orderIndex || 0,
      educationCode: institutedata?.educationCode || null,
      isPartner: institutedata?.isPartner || false,
      isActive: institutedata?.isActive || false,
      isOnshore: institutedata?.isOnshore || false,
      whyDetals: institutedata?.whyDetals || "",
      overview: institutedata?.overview || "",
      areasOfStudy: institutedata?.areasOfStudy || "",
      DestinationCountryNameColorCode:
        institutedata?.destinationCountryNameColorCode || "#000",
      UniversityNameColorCode: institutedata?.universityNameColorCode || "#000",
      isOnshoreContent: institutedata?.isOnshoreContent || "",
    },
    onSubmit: async (values) => {
      const payload = {
        ...values,
        logoMediaId: logoMedia ? logoMedia.id : null,
        bannerMediaId: bannerMedia ? bannerMedia.id : null,

        // locations are already in values.locations
      };

      try {
        if (id) {
          await UniversityService.update(id, payload);
        } else {
          await UniversityService.create(payload);
        }

        toast.success("Institute saved successfully");
        navigate("/institute");
      } catch {
        toast.error("Something went wrong");
      }
    },
  });

  /* ---------------- MEDIA ---------------- */
  const openMedia = (type) => {
    setActiveMediaType(type);
    setIsMediaOpen(true);
  };

  const handleMediaSelect = (media) => {
    activeMediaType === "logo" ? setLogoMedia(media) : setBannerMedia(media);
    setIsMediaOpen(false);
  };

  /* ---------------- STEP VALIDATION ---------------- */
  const canProceed = () => {
    switch (activeTab) {
      case "Basic":
        return (
          formik.values.name &&
          formik.values.slug &&
          isSlugValid &&
          formik.values.destinationCountryId &&
          formik.values.DestinationCountryNameColorCode &&
          formik.values.UniversityNameColorCode
        );
      // case "Intakes":
      //   return formik.values.intakes.every(
      //     (int) => int.heading && int.description
      //   );
      // case "Location":
      //   return formik.values.locations.every(
      //     (loc) => loc.heading && loc.description
      //   );

      // case "Content":
      //   return (
      //     formik.values.whyDetals &&
      //     formik.values.overview
      //   );
      case "Media":
        return !!logoMedia && !!bannerMedia;

      default:
        return true;
    }
  };

  /* ---------------- NAVIGATION ---------------- */
  const nextTab = () => {
    const idx = tabs.indexOf(activeTab);
    if (idx < tabs.length - 1 && canProceed()) {
      setActiveTab(tabs[idx + 1]);
    }
  };

  const prevTab = () => {
    const idx = tabs.indexOf(activeTab);
    if (idx > 0) setActiveTab(tabs[idx - 1]);
  };
  const breadcrumbItems = [{ title: "Back to List", link: "/institute" }];

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Institute Form"
          pageTitle="Institute"
          breadcrumbItems={breadcrumbItems}
        />

        <Card>
          <CardBody>
            {/* Tab Nav */}
            <Nav tabs className="mb-3">
              {tabs.map((t) => (
                <NavItem key={t}>
                  <NavLink
                    className={activeTab === t ? "active" : ""}
                    onClick={() => setActiveTab(t)}
                    style={{ cursor: "pointer" }}
                  >
                    {t}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            <form onSubmit={formik.handleSubmit}>
              <TabContent activeTab={activeTab}>
                {/* BASIC */}
                <TabPane tabId="Basic">
                  <Row>
                    <Col md={5} className="mb-2">
                      <Row>
                        <Col lg={12}>
                          <Label>Name</Label>
                          <Input
                            value={formik.values.name}
                            onChange={(e) => {
                              formik.setFieldValue("name", e.target.value);
                              if (!id) {
                                const slug = generateSlug(e.target.value);
                                formik.setFieldValue("slug", slug);
                                checkSlug(slug);
                              }
                            }}
                          />
                        </Col>
                        <Col md={12} className="mb-2">
                          <Label>Slug</Label>
                          <Input
                            invalid={!isSlugValid}
                            value={formik.values.slug}
                            onBlur={(e) => checkSlug(e.target.value)}
                            onChange={(e) =>
                              formik.setFieldValue("slug", e.target.value)
                            }
                          />
                          <small
                            className={
                              isSlugValid ? "text-success" : "text-danger"
                            }
                          >
                            {slugMessage}
                          </small>
                        </Col>

                        <Col md={12} className="mb-2">
                          <Label>Destination Country</Label>
                          <Input
                            type="select"
                            value={formik.values.destinationCountryId}
                            onChange={formik.handleChange}
                            name="destinationCountryId"
                          >
                            <option value="">Select</option>
                            {destinationdata.map((d) => (
                              <option key={d.id} value={d.id}>
                                {d.name}
                              </option>
                            ))}
                          </Input>
                        </Col>

                        <Col md={12} className="mb-2">
                          <Label>City</Label>
                          <Input
                            name="city"
                            value={formik.values.city}
                            onChange={formik.handleChange}
                          />
                        </Col>
                        <Col md={6} className="mb-2">
                          <Label>Education Code</Label>
                          <Input
                            type="text"
                            name="educationCode"
                            value={formik.values.educationCode}
                            onChange={formik.handleChange}
                          />
                        </Col>
                        <Col md={6} className="mb-2">
                          <Label>Order Index</Label>
                          <Input
                            type="number"
                            name="orderIndex"
                            value={formik.values.orderIndex}
                            onChange={formik.handleChange}
                          />
                        </Col>
                      </Row>
                    </Col>

                    <Col md={7}>
                      <div className="d-flex gap-5">
                        <FormGroup>
                          <Label>Name Text Color</Label>
                          <ColorPicker
                            value={formik.values.UniversityNameColorCode}
                            onChange={(color) =>
                              formik.setFieldValue(
                                "UniversityNameColorCode",
                                color,
                              )
                            }
                            isToggle={true}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>Country Text Color</Label>
                          <ColorPicker
                            value={
                              formik.values.DestinationCountryNameColorCode
                            }
                            onChange={(color) =>
                              formik.setFieldValue(
                                "DestinationCountryNameColorCode",
                                color,
                              )
                            }
                            isToggle={true}
                          />
                        </FormGroup>
                      </div>
                    </Col>
                  </Row>
                </TabPane>
                {/* <TabPane tabId="Intakes">
                  {formik.values.intakes.map((intake, index) => (
                    <div key={index} className="mb-3  p-2 rounded">
                      <Row>
                        <Col>
                          <Input
                            placeholder="Heading"
                            value={intake.heading}
                            onChange={(e) =>
                              formik.setFieldValue(
                                `intakes[${index}].heading`,
                                e.target.value
                              )
                            }
                            className="mb-3"
                          />
                        </Col>
                        <Col>
                          <Input
                            placeholder="Order Index"
                            value={intake.orderIndex}
                            onChange={(e) =>
                              formik.setFieldValue(
                                `intakes[${index}].orderIndex`,
                                e.target.value
                              )
                            }
                            className="mb-3"
                          />
                        </Col>
                      </Row>

                      <CKEditor
                        editor={ClassicEditor}
                        data={intake.description}
                        onChange={(e, editor) =>
                          formik.setFieldValue(
                            `intakes[${index}].description`,
                            editor.getData()
                          )
                        }
                        config={{
                          toolbar: [
                            "heading",
                            "|",
                            "bold",
                            "italic",
                            "underline",
                            "strikethrough",
                            "|",
                            "link",
                            "bulletedList",
                            "numberedList",
                            "blockQuote",
                            "|",
                            "undo",
                            "redo",
                          ],
                          removePlugins: [
                            "EasyImage",
                            "ImageUpload",
                            "MediaEmbed",
                          ],
                        }}
                      />
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => {
                          const updated = [...formik.values.intakes];
                          updated.splice(index, 1);
                          formik.setFieldValue("intakes", updated);
                        }}
                        className="mt-2"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() =>
                      formik.setFieldValue("intakes", [
                        ...formik.values.intakes,
                        { heading: "", description: "" },
                      ])
                    }
                  >
                    Add Intake
                  </Button>
                </TabPane>
                <TabPane tabId="Location">
                  {formik?.values?.locations?.map((loc, index) => (
                    <div key={index} className="mb-3  p-2 rounded">
                      <Row>
                        <Col>
                          <Input
                            placeholder="Heading"
                            value={loc.heading}
                            onChange={(e) =>
                              formik.setFieldValue(
                                `locations[${index}].heading`,
                                e.target.value
                              )
                            }
                            className="mb-3"
                          />
                        </Col>
                        <Col>
                          <Input
                            placeholder="Order Index"
                            value={loc?.orderIndex}
                            onChange={(e) =>
                              formik.setFieldValue(
                                `locations[${index}].orderIndex`,
                                e.target.value
                              )
                            }
                            className="mb-3"
                          />
                        </Col>
                      </Row>

                      <CKEditor
                        editor={ClassicEditor}
                        data={loc.description}
                        onChange={(e, editor) =>
                          formik.setFieldValue(
                            `locations[${index}].description`,
                            editor.getData()
                          )
                        }
                        config={{
                          toolbar: [
                            "heading",
                            "|",
                            "bold",
                            "italic",
                            "underline",
                            "strikethrough",
                            "|",
                            "link",
                            "bulletedList",
                            "numberedList",
                            "blockQuote",
                            "|",
                            "undo",
                            "redo",
                          ],
                          removePlugins: [
                            "EasyImage",
                            "ImageUpload",
                            "MediaEmbed",
                          ],
                        }}
                      />
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => {
                          const updated = [...formik.values.locations];
                          updated.splice(index, 1);
                          formik.setFieldValue("locations", updated);
                        }}
                        className="mt-2"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() =>
                      formik.setFieldValue("locations", [
                        ...formik.values.locations,
                        { heading: "", description: "" },
                      ])
                    }
                  >
                    Add Location
                  </Button>
                </TabPane> */}

                {/* CONTENT */}
                <TabPane tabId="Content">
                  <Label>Why Details</Label>
                  <CKEditor
                    editor={ClassicEditor}
                    data={formik.values.whyDetals}
                    onChange={(e, editor) =>
                      formik.setFieldValue("whyDetals", editor.getData())
                    }
                  />

                  <Label className="mt-3">Overview</Label>
                  <CKEditor
                    editor={ClassicEditor}
                    data={formik.values.overview}
                    onChange={(e, editor) =>
                      formik.setFieldValue("overview", editor.getData())
                    }
                  />

                  <Label className="mt-3">Areas Of Study</Label>
                  <CKEditor
                    editor={ClassicEditor}
                    data={formik.values.areasOfStudy}
                    onChange={(e, editor) =>
                      formik.setFieldValue("areasOfStudy", editor.getData())
                    }
                  />
                </TabPane>

                {/* isOnshore Content */}
                <TabPane tabId="IsOnshoreContent">
                  <Label>Onshore Content</Label>
                  <CKEditor
                    editor={ClassicEditor}
                    data={formik.values.isOnshoreContent}
                    onChange={(e, editor) =>
                      formik.setFieldValue("isOnshoreContent", editor.getData())
                    }
                  />
                </TabPane>

                {/* MEDIA */}
                <TabPane tabId="Media">
                  <Row>
                    {/* Logo */}
                    <Col md={6} className="mb-3">
                      <Label>Logo</Label>
                      <br />

                      {logoMedia ? (
                        <div className="text-center p-2">
                          <div
                            style={{
                              maxHeight: "150px",
                              width: "200px",
                              margin: "0 auto",
                              overflow: "hidden",
                              borderRadius: "8px",
                              border: "1px solid #ddd",
                            }}
                            className="mb-3"
                          >
                            <img
                              src={`${imageBaseApi}${logoMedia.url}`}
                              alt="Logo"
                              style={{ width: "100%", objectFit: "contain" }}
                            />
                          </div>

                          <div className="fw-semibold">{logoMedia.name}</div>

                          {logoMedia.size && (
                            <small className="text-muted">
                              Size: {(logoMedia.size / 1024).toFixed(1)} KB
                            </small>
                          )}

                          <div className="d-flex justify-content-center gap-2 mt-2">
                            <Button
                              size="sm"
                              outline
                              onClick={() => openMedia("logo")}
                            >
                              <i className="ri-edit-line"></i>
                            </Button>
                            <Button
                              size="sm"
                              outline
                              color="danger"
                              onClick={() => setLogoMedia(null)}
                            >
                              <i className="ri-delete-bin-5-line"></i>
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center p-3">
                          <i className="ri-image-add-line fs-1 text-secondary"></i>
                          <h6 className="mt-2 mb-1 text-muted">
                            No logo media selected
                          </h6>
                          <h6 className="mb-2 text-muted">256 x 126</h6>
                          <Button
                            outline
                            className="rounded-pill px-4"
                            onClick={() => openMedia("logo")}
                          >
                            Choose Logo
                          </Button>
                        </div>
                      )}
                    </Col>

                    {/* Banner */}
                    <Col md={6} className="mb-3">
                      <Label>Banner</Label>
                      <br />

                      {bannerMedia ? (
                        <div className="text-center p-3 border rounded bg-light">
                          <img
                            src={`${imageBaseApi}${bannerMedia.url}`}
                            className="img-fluid rounded"
                            alt="Banner"
                          />

                          <div className="fw-semibold mt-2">
                            {bannerMedia.name}
                          </div>

                          {bannerMedia.size && (
                            <small className="text-muted">
                              Size: {(bannerMedia.size / 1024).toFixed(1)} KB
                            </small>
                          )}

                          <div className="d-flex gap-2 mt-2 justify-content-center">
                            <Button outline onClick={() => openMedia("banner")}>
                              <i className="ri-edit-line"></i>
                            </Button>
                            <Button
                              outline
                              color="danger"
                              onClick={() => setBannerMedia(null)}
                            >
                              <i className="ri-delete-bin-5-line"></i>
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="p-4 border rounded text-center bg-light">
                          <i className="ri-image-add-line fs-1 text-secondary"></i>
                          <h6 className="mt-2 mb-1 text-muted">
                            No banner image selected
                          </h6>
                          <h6 className="mb-2 text-muted">2400 x 400</h6>
                          <Button
                            outline
                            color="secondary"
                            className="rounded-pill px-4"
                            onClick={() => openMedia("banner")}
                          >
                            <i className="ri-image-add-line me-1"></i> Choose
                            Banner Image
                          </Button>
                        </div>
                      )}
                    </Col>
                  </Row>
                </TabPane>

                {/* SETTINGS */}
                <TabPane tabId="Settings">
                  <Row>
                    <Col md={6} className="mb-2">
                      <Label>Website</Label>
                      <Input
                        name="websiteUrl"
                        value={formik.values.websiteUrl}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6} className="mb-2">
                      <Label>Course Fee URL</Label>
                      <Input
                        name="applyUrl"
                        value={formik.values.applyUrl}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6} className="mb-2">
                      <Label>Programmes URL</Label>
                      <Input
                        name="programmesUrl"
                        value={formik.values.programmesUrl}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    {/* <Col md={6} className="mb-2">
                      <Label>Button URL</Label>
                      <Input
                        name="buttonUrl"
                        value={formik.values.buttonUrl}
                        onChange={formik.handleChange}
                      />
                    </Col> */}
                    <Col md={6} className="mb-2">
                      <Label>Video URL</Label>
                      <Input
                        name="videoUrl"
                        value={formik.values.videoUrl}
                        onChange={formik.handleChange}
                      />
                    </Col>

                    <Col md={6} className="">
                      <div className="d-flex pt-lg-4 justify-content-start gap-5 align-content-center align-items-center">
                        <FormGroup>
                          <Input
                            type="checkbox"
                            checked={formik.values.isPartner}
                            onChange={(e) =>
                              formik.setFieldValue(
                                "isPartner",
                                e.target.checked,
                              )
                            }
                          />{" "}
                          <Label>Partner</Label>
                        </FormGroup>
                        <FormGroup>
                          <Input
                            type="checkbox"
                            checked={formik.values.isActive}
                            onChange={(e) =>
                              formik.setFieldValue("isActive", e.target.checked)
                            }
                          />{" "}
                          <Label>Active</Label>
                        </FormGroup>
                        <FormGroup>
                          <Input
                            type="checkbox"
                            checked={formik.values.isOnshore}
                            onChange={(e) =>
                              formik.setFieldValue(
                                "isOnshore",
                                e.target.checked,
                              )
                            }
                          />{" "}
                          <Label>Onshore</Label>
                        </FormGroup>
                      </div>
                    </Col>
                  </Row>
                </TabPane>
              </TabContent>

              {/* NAV BUTTONS */}
              <div className="d-flex justify-content-between mt-4">
                <Button
                  disabled={tabs.indexOf(activeTab) === 0}
                  onClick={prevTab}
                >
                  Previous
                </Button>

                {tabs.indexOf(activeTab) < tabs.length - 1 ? (
                  <Button
                    color="success"
                    disabled={!canProceed()}
                    onClick={nextTab}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    color="success"
                    type="button" // IMPORTANT: change from type="submit" to button
                    disabled={!canProceed()}
                    onClick={() => formik.handleSubmit()} // manual submit only when tab validation passes
                  >
                    Submit
                  </Button>
                )}
              </div>
            </form>
          </CardBody>
        </Card>

        <MediaModal
          isOpen={isMediaOpen}
          toggle={() => setIsMediaOpen(false)}
          mediaData={mediaData}
          selectedMedia={activeMediaType === "logo" ? logoMedia : bannerMedia}
          setSelectedMedia={handleMediaSelect}
        />
      </Container>
    </div>
  );
};

export default AddInstitute;
